import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { getLivistheme, getLivisBrandColor, getLivisBrandTheme } from '../../helpers/storage';
import { brandColors, changeTheme, changeBrandColor } from '../../helpers/globalTheme';
import { useTranslation } from 'react-i18next';

type ThemeMode = 'light' | 'dark';

const ThemeSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const [currentThemeMode, setCurrentThemeMode] = useState<ThemeMode>('light');
  const [currentBrandColor, setCurrentBrandColor] = useState<string>('#0857DE');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFixed, setIsFixed] = useState(false);
  const [position, setPosition] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const dragOffset = useRef<{ y: number }>({ y: 0 });
  const rafId = useRef<number | null>(null);
  useEffect(() => {
    // Get current theme state from localStorage
    const savedTheme = getLivistheme() as ThemeMode; // Get the theme mode, not brand theme
    const savedColor = getLivisBrandColor();
    const savedBrandTheme = getLivisBrandTheme();

    // Determine theme mode from brand theme if available
    let themeMode: ThemeMode = 'light';
    if (savedBrandTheme) {
      themeMode = savedBrandTheme.includes('-dark-') ? 'dark' : 'light';
    } else if (savedTheme) {
      themeMode = savedTheme;
    }

    const validColor = brandColors.find(brand => brand.value === savedColor)?.value || '#0857de';

    setCurrentThemeMode(themeMode);
    setCurrentBrandColor(validColor);
  }, [t]);
  
  const handlePointerDown = (e: React.PointerEvent) => {
    if (!containerRef.current) return;
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[role="button"]') ||
      target.closest('input, select, textarea')
    ) {
      return;
    }
    if (e.button !== 0) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (!isFixed) {
      const rightFromEdge = window.innerWidth - rect.right;
      setPosition({ top: rect.top, right: rightFromEdge });
      setIsFixed(true);
    }
    dragOffset.current = { y: e.clientY - rect.top };
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onPointerMove = (e: PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        const { height } = el.getBoundingClientRect();
        const maxTop = Math.max(0, window.innerHeight - height);
        let newTop = e.clientY - dragOffset.current.y;

        newTop = Math.min(Math.max(0, newTop), maxTop);
        setPosition(prev => ({ top: newTop, right: prev.right }));
      });
    };
    const onPointerUp = () => {
      setIsDragging(false);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, [isDragging]);
  
  // Handle theme mode change (light/dark toggle)
  const handleThemeChange = (event: React.MouseEvent<HTMLElement>, newTheme: ThemeMode | null): void => {
    if (newTheme !== null) {
      setCurrentThemeMode(newTheme);
      changeTheme(newTheme);
    }
  };

  // Handle brand color change
  const handleColorChange = (color: string): void => {
    setCurrentBrandColor(color);
    changeBrandColor(color);
  };


  return (
    <Box
      className="livis-switch-theme-container"
      ref={containerRef}
      onPointerDown={handlePointerDown}
      sx={{
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        willChange: 'top',
        transition: 'none',
        position: isFixed ? 'fixed' : undefined,
        top: isFixed ? position.top : undefined,
        right: isFixed ? position.right : undefined,
        zIndex: 1300,
      }}
    >
      {/* Always visible theme icon */}
      <Box className="livis-switch-theme-icon">
        {currentThemeMode === 'light' ? (
          <LightModeIcon fontSize="small" />
        ) : (
          <DarkModeIcon fontSize="small" />
        )}
      </Box>

      {/* Expanded content - only visible on hover */}
      <Box className="livis-switch-theme-expanded">
         <Box className="livis-switch-theme-toggle-section">
          <ToggleButtonGroup
            value={currentThemeMode}
            exclusive
            onChange={handleThemeChange}
            aria-label="theme selector"
            className="livis-switch-theme-toggle-group"
          >
            <ToggleButton
              value="light"
              aria-label="light theme"
              className="livis-switch-theme-toggle-button"
            >
              <LightModeIcon fontSize="small" className='livis-switch-theme-toggle-button-icon' />
            </ToggleButton>
            <ToggleButton
              value="dark"
              aria-label="dark theme"
              className="livis-switch-theme-toggle-button"
            >
              <DarkModeIcon fontSize="small" className='livis-switch-theme-toggle-button-icon' />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box> 

        <Box className="livis-switch-theme-color-section">
          <Box className="livis-switch-theme-colors-grid">
            <Grid container spacing={0.5}>
              {brandColors.map((color: { name: React.Key | null | undefined; value: string; }) => (
                <Grid item xs={3} key={color.name}>
                  <Button
                    onClick={() => handleColorChange(color.value)}
                    className={`livis-switch-theme-color-option ${currentBrandColor === color.value ? 'livis-switch-theme-color-selected' : ''
                      }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name ? String(color.name) : ''}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ThemeSwitcher;